/**
 * Professional ARTs Section
 * Bloco de ARTs (Anotação de Responsabilidade Técnica) do profissional (#345).
 *
 * Vive na FICHA (`/professionals/:id`), como bloco de relacionados. Antes
 * ficava pendurado abaixo do formulário de edição, e ali havia duas semânticas
 * de salvamento na mesma tela: o formulário só grava no botão final, enquanto
 * cada ação de ART persiste na hora. Quem excluía uma ART e clicava em
 * "Cancelar" acreditava ter desfeito a exclusão — e nada na tela dizia o
 * contrário.
 *
 * O bloco é uma LISTA COMPACTA, não uma tabela. Uma tabela de seis colunas mais
 * uma de ações é uma tela de listagem inteira dentro de uma ficha: destoava dos
 * blocos temáticos ao lado, que são densos, e roubava a página para si. As
 * quatro ações moram num menu por item, para não virar quatro botões soltos em
 * cada linha.
 *
 * A situação (Válida / Vencida / Sem validade) é derivada de `expiryDate` na
 * renderização — não existe coluna de status no banco.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
  Description as ArtIcon,
  Edit as EditIcon,
  LinkOff as LinkOffIcon,
  MoreVert as MoreVertIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { SectionCard } from '../../../components';
import { useAcao } from '../../../authorization/useVeredito';
import { ActionButton } from '../../../components/common';
import { professionalArtService } from '../../../services/professionalArtService';
import { companyService } from '../../../services/companyService';
import { getApiErrorMessage } from '../../../types/common';
import { artStatus, type ArtStatus, type ProfessionalArt, type ProfessionalArtInput } from '../../../types/professionalArt';
import ProfessionalArtFormModal, { type CompanyOption } from './ProfessionalArtFormModal';

export interface ProfessionalArtsSectionProps {
  professionalId: number | string;
}

/**
 * `issueDate` e `expiryDate` são DATEONLY: chegam como 'YYYY-MM-DD'. Formatar
 * via `new Date()` jogaria o dia para trás em fuso negativo (a string é lida
 * como UTC e exibida em horário local), então a conversão é textual.
 */
const formatarData = (iso: string | null): string => {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  if (!ano || !mes || !dia) return '—';
  return `${dia}/${mes}/${ano}`;
};

const ROTULO_SITUACAO: Record<ArtStatus, string> = {
  valida: 'Válida',
  vencida: 'Vencida',
  sem_validade: 'Sem validade',
};

const COR_SITUACAO: Record<ArtStatus, 'success' | 'error' | 'default'> = {
  valida: 'success',
  vencida: 'error',
  sem_validade: 'default',
};


const ProfessionalArtsSection: React.FC<ProfessionalArtsSectionProps> = ({ professionalId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  /*
    O backend separa as duas permissões: excluir a ART exige
    `professional.delete`, e criar, editar, anexar e desanexar exigem
    `professional.update` (ver `routes/professionals.ts`). Sem este espelho na
    tela, quem só lê via os quatro botões e só descobria a falta de permissão
    no 403.
  */
  const editar = useAcao('professional.update', 'Editar');
  const enviarArquivo = useAcao('professional.update', 'Enviar arquivo');
  const removerAnexo = useAcao('professional.update', 'Remover anexo');
  const excluir = useAcao('professional.delete', 'Excluir');

  const [arts, setArts] = useState<ProfessionalArt[]>([]);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ProfessionalArt | null>(null);
  const [toDelete, setToDelete] = useState<ProfessionalArt | null>(null);
  const [toDetach, setToDetach] = useState<ProfessionalArt | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuArt, setMenuArt] = useState<ProfessionalArt | null>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetRef = useRef<ProfessionalArt | null>(null);

  const carregarArts = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      setArts(await professionalArtService.list(professionalId));
    } catch (err: unknown) {
      enqueueSnackbar(getApiErrorMessage(err, 'Erro ao carregar ARTs'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [professionalId, enqueueSnackbar]);

  useEffect(() => {
    void carregarArts();
  }, [carregarArts]);

  useEffect(() => {
    const carregarEmpresas = async (): Promise<void> => {
      try {
        setCompanies(await companyService.getCompaniesForDropdown());
      } catch (err: unknown) {
        enqueueSnackbar(getApiErrorMessage(err, 'Erro ao carregar empresas'), { variant: 'error' });
      }
    };
    void carregarEmpresas();
  }, [enqueueSnackbar]);

  const nomeDaEmpresa = (art: ProfessionalArt): string =>
    art.companyName ?? companies.find((company) => company.id === art.companyId)?.name ?? '—';

  const handleDownload = async (art: ProfessionalArt): Promise<void> => {
    try {
      await professionalArtService.downloadFile(professionalId, art.id, art.originalName ?? undefined);
    } catch (err: unknown) {
      enqueueSnackbar(getApiErrorMessage(err, 'Erro ao baixar o arquivo da ART'), { variant: 'error' });
    }
  };

  const handleSubmit = async (data: ProfessionalArtInput, file: File | null): Promise<void> => {
    // O corpo do PUT não leva companyId: o backend recusa a troca.
    const salva = editing
      ? await professionalArtService.update(professionalId, editing.id, {
          artNumber: data.artNumber,
          issueDate: data.issueDate,
          expiryDate: data.expiryDate,
          notes: data.notes,
        })
      : await professionalArtService.create(professionalId, data);

    if (file) {
      await professionalArtService.uploadFile(professionalId, salva.id, file);
    }

    enqueueSnackbar(editing ? 'ART atualizada com sucesso!' : 'ART cadastrada com sucesso!', {
      variant: 'success',
    });
    await carregarArts();
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!toDelete) return;
    try {
      await professionalArtService.remove(professionalId, toDelete.id);
      enqueueSnackbar('ART removida com sucesso!', { variant: 'success' });
      await carregarArts();
    } catch (err: unknown) {
      enqueueSnackbar(getApiErrorMessage(err, 'Erro ao remover ART'), { variant: 'error' });
    } finally {
      setToDelete(null);
    }
  };

  /**
   * Remove SÓ o anexo. A ART fica — número, emissão e validade continuam
   * valendo; sem isto, trocar um PDF errado obrigava a excluir a ART inteira e
   * recadastrá-la.
   */
  const handleConfirmDetach = async (): Promise<void> => {
    if (!toDetach) return;
    try {
      await professionalArtService.removeFile(professionalId, toDetach.id);
      enqueueSnackbar('Anexo removido com sucesso!', { variant: 'success' });
      await carregarArts();
    } catch (err: unknown) {
      enqueueSnackbar(getApiErrorMessage(err, 'Erro ao remover o anexo da ART'), { variant: 'error' });
    } finally {
      setToDetach(null);
    }
  };

  const handleUploadClick = (art: ProfessionalArt): void => {
    uploadTargetRef.current = art;
    uploadInputRef.current?.click();
  };

  const handleUploadChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    const art = uploadTargetRef.current;
    event.target.value = '';
    if (!file || !art) return;
    try {
      await professionalArtService.uploadFile(professionalId, art.id, file);
      enqueueSnackbar('Arquivo enviado com sucesso!', { variant: 'success' });
      await carregarArts();
    } catch (err: unknown) {
      enqueueSnackbar(getApiErrorMessage(err, 'Erro ao enviar o arquivo da ART'), { variant: 'error' });
    }
  };

  const fecharMenu = (): void => {
    setMenuAnchor(null);
    setMenuArt(null);
  };

  /**
   * Item da lista. Duas linhas de texto e, à direita, a situação e o menu — a
   * mesma densidade dos blocos temáticos vizinhos.
   */
  const renderArt = (art: ProfessionalArt): React.ReactElement => {
    const situacao = artStatus(art);
    return (
      <Box
        key={art.id}
        data-testid={`art-item-${art.id}`}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
            {art.artNumber}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {nomeDaEmpresa(art)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {`Emissão ${formatarData(art.issueDate)} · Validade ${formatarData(art.expiryDate)}`}
          </Typography>
          {art.originalName ? (
            <Link
              component="button"
              type="button"
              variant="caption"
              onClick={() => void handleDownload(art)}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%' }}
            >
              <AttachFileIcon sx={{ fontSize: 14 }} />
              {art.originalName}
            </Link>
          ) : (
            <Typography variant="caption" color="text.secondary" display="block">
              Sem anexo
            </Typography>
          )}
        </Box>
        <Chip size="small" label={ROTULO_SITUACAO[situacao]} color={COR_SITUACAO[situacao]} />
        <IconButton
          size="small"
          aria-label={`Ações da ART ${art.artNumber}`}
          onClick={(event) => {
            setMenuAnchor(event.currentTarget);
            setMenuArt(art);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  return (
    <SectionCard
      title="ARTs"
      icon={<ArtIcon />}
      accentColor={theme.palette.secondary.main}
      badge={<Chip size="small" label={arts.length} />}
      action={
        <ActionButton
          acao="professional.update"
          rotulo="Adicionar"
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          Adicionar
        </ActionButton>
      }
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : arts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nenhum registro encontrado
        </Typography>
      ) : (
        <Stack spacing={1.5} divider={<Divider flexItem />}>
          {arts.map(renderArt)}
        </Stack>
      )}

      {/*
        Um menu por item, e não quatro botões por linha: as quatro ações não
        cabem soltas num bloco de meia largura. Cada entrada respeita a
        permissão que o backend exige — excluir pede `professional.delete`, as
        demais pedem `professional.update`.
      */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={fecharMenu}>
        <MenuItem
          disabled={editar.disabled}
          onClick={() => {
            setEditing(menuArt);
            setModalOpen(true);
            fecharMenu();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{editar.tooltip}</ListItemText>
        </MenuItem>
        <MenuItem
          disabled={enviarArquivo.disabled}
          onClick={() => {
            if (menuArt) handleUploadClick(menuArt);
            fecharMenu();
          }}
        >
          <ListItemIcon>
            <UploadFileIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{enviarArquivo.tooltip}</ListItemText>
        </MenuItem>
        {menuArt?.originalName && (
          <MenuItem
            disabled={removerAnexo.disabled}
            onClick={() => {
              setToDetach(menuArt);
              fecharMenu();
            }}
          >
            <ListItemIcon>
              <LinkOffIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{removerAnexo.tooltip}</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          disabled={excluir.disabled}
          onClick={() => {
            setToDelete(menuArt);
            fecharMenu();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>{excluir.tooltip}</ListItemText>
        </MenuItem>
      </Menu>

      <input
        ref={uploadInputRef}
        type="file"
        hidden
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={(event) => void handleUploadChange(event)}
      />

      <ProfessionalArtFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        art={editing}
        companies={companies}
      />

      <Dialog open={!!toDetach} onClose={() => setToDetach(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remover anexo</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remover o arquivo {toDetach?.originalName} da ART {toDetach?.artNumber}? Ao
            confirmar, o anexo sai na hora. A ART continua cadastrada — número, emissão
            e validade seguem valendo.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDetach(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={() => void handleConfirmDetach()}>
            Remover anexo do registro
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!toDelete} onClose={() => setToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir ART</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Excluir a ART {toDelete?.artNumber}? Ao confirmar, ela é apagada na hora
            — não há &quot;Salvar&quot; depois disto, e a ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={() => void handleConfirmDelete()}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </SectionCard>
  );
};

export default ProfessionalArtsSection;
